import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar, Toolbar, Box, IconButton, Tooltip, Typography, useTheme,
  Menu, MenuItem, Stack, Badge
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import BookmarkAddedRoundedIcon from "@mui/icons-material/BookmarkAddedRounded";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import ThemeToggleButton from "./ThemeToggleButton";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useMediaQuery from "@mui/material/useMediaQuery";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import Chip from "@mui/material/Chip";
import api from "../services/api";
import FeatureGateDialog from "./modals/FeatureGateDialog";
import { userHasActiveEntitlement } from "../utils/entitlements";
// LocationGate is rendered globally in Layout
import { useLocationCtx } from "../context/LocationContext";
import { pathLangFrom, canonicalI18n } from "../utils/lang";

const LANGS = [
  { code: "el", label: "Ελληνικά", icon: "🇬🇷" },
  { code: "en", label: "English", icon: "🇬🇧" },
];


const NavBar = ({ onOpenLocationGate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, logout, requestOnboardingRerun } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { country, usZip, usState } = useLocationCtx();

  const [langMenu, setLangMenu] = useState(null);
  const [watchGateOpen, setWatchGateOpen] = useState(false);
  const [watchSummary, setWatchSummary] = useState({ followedCount: 0, recentChanges: 0 });

  const langs = LANGS;
  const currentLang = langs.find(l => l.code === i18n.language) || langs[0];

  const langPrefix = useMemo(() => pathLangFrom(i18n.language), [i18n.language]);
  const isPremium = useMemo(() => userHasActiveEntitlement(user), [user]);
  const priceWatchPath = `/${langPrefix}/price-watch`;
  const displayWatchCount = watchSummary.recentChanges > 0 ? watchSummary.recentChanges : watchSummary.followedCount;

  useEffect(() => {
    let active = true;
    if (!user || !isPremium) {
      setWatchSummary({ followedCount: 0, recentChanges: 0 });
      return undefined;
    }

    api.followedGeneric
      .list()
      .then((res) => {
        if (!active) return;
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        const now = Date.now();
        const recentChanges = items.filter((item) => {
          const ts = item?.price_changed_at ? new Date(item.price_changed_at).getTime() : 0;
          return ts > 0 && now - ts <= 7 * 24 * 60 * 60 * 1000;
        }).length;
        setWatchSummary({
          followedCount: items.length,
          recentChanges,
        });
      })
      .catch(() => {
        if (!active) return;
        setWatchSummary({ followedCount: 0, recentChanges: 0 });
      });

    return () => {
      active = false;
    };
  }, [user, isPremium]);

  const handleLogin = () => navigate(`/${langPrefix}/login`);
  const handleOpenPriceWatch = () => {
    if (user && isPremium) {
      navigate(priceWatchPath);
      return;
    }
    setWatchGateOpen(true);
  };
  const handleRestartTour = () => {
    if (!user?.id) return;
    if (typeof requestOnboardingRerun === "function") {
      requestOnboardingRerun(user.id);
    }
    navigate(`/${langPrefix}/client/dashboard?tour=1`);
  };
  const handleLangMenu = (e) => setLangMenu(e.currentTarget);

  const handleLangSelect = (code) => {
    const canon = canonicalI18n(code);
    i18n.changeLanguage(canon);
    localStorage.setItem("language", canon);
    setLangMenu(null);

    const newPrefix = pathLangFrom(canon);
    const newPath = window.location.pathname.replace(/^\/[a-z]{2}\b/, `/${newPrefix}`);
    // αν δεν είχε prefix, πρόσθεσέ το
    const finalPath = newPath === window.location.pathname
      ? `/${newPrefix}${window.location.pathname.startsWith("/") ? "" : "/"}${window.location.pathname}`
      : newPath;
    // κράτα και το query/hash
    const full = `${finalPath}${window.location.search || ""}${window.location.hash || ""}`;
    navigate(full, { replace: true });
  };


  const navItems = [
    {
      icon: <HomeIcon />,
      label: t("home", "Αρχική"),
      path: `/${langPrefix}`,                 // ✅ Landing
    },
    {
      icon: <DashboardCustomizeIcon />,
      label: t("dashboard", "Πίνακας"),
      path: `/${langPrefix}/client/dashboard`,       // ✅ Dashboard
    },
    {
      icon: <FlashOnIcon />,
      label: t("quick_recipe"),
      path: `/${langPrefix}/quick-recipe`,
    },
{
  icon: <ShoppingCartIcon />,
  label: t("shopping_best_prices") || "Best Prices",
  path: `/${langPrefix}/shopping-custom/list`,
},

    {
      icon: <SearchIcon />,
      label: t("all_recipes"),
      path: `/${langPrefix}/search-recipes`,
    },
    {
      icon: <SavingsOutlinedIcon />,
      label: t("budget_recipes.nav_label", "On your budget"),
      path: `/${langPrefix}/budget-recipes`,
    },
    // μπορείς να ξανα-ενεργοποιήσεις αυτά όταν έρθει η ώρα:
    // { icon: <MenuBookIcon />,  label: t("my_recipes"),  path: `/${langPrefix}/client/recipes` },
    // { icon: <PostAddIcon />,   label: t("add_recipe"),  path: `/${langPrefix}/client/add-recipe` },
  ];

  const countryLabel = (() => {
    if (!country) return t("set_location") || "Set location";
    if (String(country) === "2") {
      const parts = ["US", usState || null, usZip || null].filter(Boolean);
      return parts.join(" ");
    }
    if (String(country) === "1") return "GR";
    return `Country #${country}`;
  })();

  return (
    <AppBar
      position="static"
      elevation={2}
      sx={{
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 16,
        boxShadow: theme.palette.mode === "dark"
          ? "0 4px 24px #49bc9080"
          : "0 2px 14px #ffa84d18",
        transition: "background 0.2s"
      }}
    >
      {/* === Πάνω γραμμή (Logo, όνομα, lang, theme, login/logout, location chip) === */}
      <Toolbar
        sx={{
          minHeight: { xs: 38, sm: 48 },
          px: { xs: 1, sm: 3 },
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between"
        }}
      >
        {/* Logo / Όνομα */}
<Box
  component={Link}
  to={`/${langPrefix}`}
  sx={{
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    minWidth: 0, // ✅ για να δουλέψει ellipsis
    gap: 1,
  }}
>
  <Box
    component="img"
    src="/logo.png"         // ή "/company_logo.png"
    alt="InCooking"
    onError={(e) => { e.currentTarget.src = "/favicon.ico"; }}
    sx={{
      height: { xs: 34, sm: 40 },
      width: { xs: 34, sm: 40 },
      borderRadius: 2,
      boxShadow: 2,
      flex: "0 0 auto",
    }}
  />

  <Typography
    variant="h6"
    sx={{
      display: { xs: "none", sm: "block" }, // ✅ κρύβεται στο mobile
      color: theme.palette.primary.main,
      fontWeight: 900,
      letterSpacing: 0.6,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: 180,
    }}
  >
    InCooking
  </Typography>
</Box>


        {/* Δεξιά actions */}
        <Stack direction="row" spacing={1} alignItems="center">
          {/* Location chip ΜΕΣΑ στη toolbar (όχι overlay) */}
{isMobile ? (
  <Tooltip title={countryLabel}>
    <IconButton
      onClick={() => onOpenLocationGate && onOpenLocationGate()}
      color="inherit"
      size="small"
      sx={{
        border: "2px solid",
        borderColor: theme.palette.mode === "dark" ? "#49bc9099" : "#009e7590",
        bgcolor: theme.palette.mode === "dark" ? "#232a34" : "#e2fff3",
      }}
      aria-label="location"
    >
      <RoomOutlinedIcon />
    </IconButton>
  </Tooltip>
) : (
  <Chip
    icon={<RoomOutlinedIcon />}
    label={countryLabel}
    onClick={() => onOpenLocationGate && onOpenLocationGate()}
    variant="outlined"
    sx={{
      fontWeight: 700,
      borderColor: theme.palette.mode === "dark" ? "#49bc9099" : "#009e7590",
      bgcolor: theme.palette.mode === "dark" ? "#232a34" : "#e2fff3",
    }}
  />
)}

          <Tooltip title={t("choose_language")}>
            <IconButton onClick={handleLangMenu} color="inherit" sx={{ fontSize: 27 }}>
              <span style={{ fontSize: 26 }}>{currentLang.icon}</span>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={langMenu}
            open={Boolean(langMenu)}
            onClose={() => setLangMenu(null)}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 3,
                minWidth: 125,
                boxShadow: "0 6px 24px #49bc9090",
                bgcolor: "background.paper",
              }
            }}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            {langs.map(lang => (
              <MenuItem
                key={lang.code}
                selected={i18n.language === lang.code}
                onClick={() => handleLangSelect(lang.code)}
                sx={{ py: 1.1, fontWeight: 700, fontSize: 17 }}
              >
                <span style={{ fontSize: 22, marginRight: 7 }}>{lang.icon}</span>
                {lang.label}
              </MenuItem>
            ))}
          </Menu>

          <ThemeToggleButton />

          <Tooltip title={t("price_watch_title") || "Price Watch"}>
            <IconButton
              color="inherit"
              onClick={handleOpenPriceWatch}
              size="medium"
              sx={{
                border: "2px solid",
                borderColor: theme.palette.mode === "dark"
                  ? "#49bc9099"
                  : "#009e7590",
                bgcolor: theme.palette.mode === "dark"
                  ? "#232a34"
                  : "#eefcf6",
                '&:hover': {
                  bgcolor: theme.palette.mode === "dark"
                    ? "#233b38"
                    : "#def8ee"
                }
              }}
              aria-label={t("price_watch_title") || "Price Watch"}
            >
              <Badge
                badgeContent={displayWatchCount > 0 ? displayWatchCount : null}
                color="warning"
                overlap="circular"
                max={99}
              >
                <BookmarkAddedRoundedIcon fontSize="large" />
              </Badge>
            </IconButton>
          </Tooltip>

          {user ? (
            <Tooltip title={t("restart_tour") || "Restart tour"}>
              <IconButton
                color="inherit"
                onClick={handleRestartTour}
                size="medium"
                sx={{
                  border: "2px solid",
                  borderColor: theme.palette.mode === "dark"
                    ? "#49bc9099"
                    : "#009e7590",
                  bgcolor: theme.palette.mode === "dark"
                    ? "#232a34"
                    : "#e2fff3",
                  '&:hover': {
                    bgcolor: theme.palette.mode === "dark"
                      ? "#233b38"
                      : "#d8faeb"
                  }
                }}
                aria-label={t("restart_tour") || "Restart tour"}
              >
                <RestartAltIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          ) : null}

          {user ? (
            <Tooltip title={t("logout", "Αποσύνδεση")}>
              <IconButton
                color="inherit"
                onClick={logout}
                size="medium"
                sx={{
                  border: "2px solid",
                  borderColor: theme.palette.mode === "dark"
                    ? "#49bc9099"
                    : "#009e7590",
                  bgcolor: theme.palette.mode === "dark"
                    ? "#232a34"
                    : "#e2fff3",
                  '&:hover': {
                    bgcolor: theme.palette.mode === "dark"
                      ? "#233b38"
                      : "#d8faeb"
                  }
                }}
                aria-label={t("logout", "Αποσύνδεση")}
              >
                <LogoutIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={t("login", "Είσοδος")}>
              <IconButton
                color="inherit"
                onClick={handleLogin}
                size="large"
                sx={{
                  border: "2px solid",
                  borderColor: theme.palette.mode === "dark"
                    ? "#49bc9099"
                    : "#ffa84d99",
                  bgcolor: theme.palette.mode === "dark"
                    ? "#232a34"
                    : "#fff7ea",
                  '&:hover': {
                    bgcolor: theme.palette.mode === "dark"
                      ? "#233b38"
                      : "#ffeed8"
                  }
                }}
                aria-label={t("login", "Είσοδος")}
              >
                <LoginIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Toolbar>

      {/* === Κάτω γραμμή (navigation icons) === */}
      <Toolbar
        sx={{
          minHeight: { xs: 38, sm: 48 },
          px: { xs: 0.5, sm: 1.5 },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%"
        }}
      >
        <Stack
          direction="row"
          spacing={isMobile ? 0.1 : 2}
          sx={{
            width: "100%",
            justifyContent: "space-evenly",
            alignItems: "center"
          }}
        >
          {navItems.map(item => (
            <Tooltip key={item.path} title={item.label}>
              {(() => {
                const isActive =
                  location.pathname === item.path ||
                  location.pathname.startsWith(`${item.path}/`);
                return (
              <IconButton
                color="inherit"
                component={Link}
                to={item.path}
                size="large"
                sx={{
                  borderRadius: 3,
                  flex: 1,
                  transition: "all 0.13s",
                  px: { xs: 0.5, sm: 1.2 },
                  py: { xs: 1, sm: 0.9 },
                  bgcolor: isActive
                    ? (theme.palette.mode === "dark" ? "rgba(73,188,144,0.18)" : "rgba(0,158,117,0.12)")
                    : "transparent",
                  color: isActive ? theme.palette.primary.main : "inherit",
                  '&:hover': {
                    bgcolor: isActive
                      ? (theme.palette.mode === "dark" ? "rgba(73,188,144,0.24)" : "rgba(0,158,117,0.16)")
                      : theme.palette.action.hover,
                  }
                }}
                aria-label={item.label}
              >
                <Stack
                  direction={isMobile ? "column" : "row"}
                  spacing={isMobile ? 0.25 : 0.75}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ width: "100%" }}
                >
                  {item.icon}
                  {!isMobile ? (
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: isActive ? 800 : 700,
                        lineHeight: 1.1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.label}
                    </Typography>
                  ) : null}
                </Stack>
              </IconButton>
                );
              })()}
            </Tooltip>
          ))}
        </Stack>
      </Toolbar>

      <FeatureGateDialog
        open={watchGateOpen}
        onClose={() => setWatchGateOpen(false)}
        lang={langPrefix}
        isLoggedIn={Boolean(user)}
        require={user ? "premium" : "auth"}
        title={user ? (t("premium_feature_title") || "Premium feature") : undefined}
        message={
          user
            ? (t("price_watch_premium_gate") || "Track product price drops, review recent changes, and keep your best offers in one Premium space.")
            : (t("price_watch_auth_gate") || "Create an account to follow products and unlock Premium price intelligence.")
        }
        onLogin={() => {
          setWatchGateOpen(false);
          navigate(`/${langPrefix}/login`);
        }}
        onRegister={() => {
          setWatchGateOpen(false);
          navigate(`/${langPrefix}/register`);
        }}
        onUpgrade={() => {
          setWatchGateOpen(false);
          navigate(`/${langPrefix}/plans`);
        }}
      />
    </AppBar>
  );
};

export default NavBar;
